import { useState, useEffect, useCallback } from "react";
import { X, Layers3, GitFork, Scissors, Split, RotateCcw, Lock, Cable } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { COLOR_HEX, fiberColorName } from "@/lib/modules";
import { useSettings } from "@/context/SettingsContext";
import { useI18n } from "@/context/I18nContext";

export default function TrayView({ closure, onClose, role, onFibers }) {
  const { t } = useI18n();
  const { settings } = useSettings();
  const colorForFiber = (n) => COLOR_HEX[fiberColorName(settings, null, n)] || "#94A3B8";
  const [splices, setSplices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passThrough, setPassThrough] = useState([]);
  const [connectedCables, setConnectedCables] = useState([]);
  const [nearbyCables, setNearbyCables] = useState([]);
  const [sugA, setSugA] = useState("");
  const [sugB, setSugB] = useState("");
  const [sugg, setSugg] = useState(null);
  const canEdit = role !== "odczyt";

  const loadPassThrough = useCallback(() => {
    api.get(`/closures/${closure.id}/pass-through`)
      .then((r) => setPassThrough(r.data.pass_through || []))
      .catch(() => setPassThrough([]));
  }, [closure.id]);

  useEffect(() => {
    api.get("/splices").then((r) => setSplices(r.data.filter((s) => s.closure_id === closure.id))).finally(() => setLoading(false));
    loadPassThrough();
    api.get(`/closures/${closure.id}/cables`).then((r) => {
      setConnectedCables(r.data.cables || []);
      setNearbyCables(r.data.nearby || []);
    }).catch(() => { setConnectedCables([]); setNearbyCables([]); });
  }, [closure.id, loadPassThrough]);

  const doCut = async (cableId, fiber, confirmForce = false) => {
    try {
      const { data } = await api.post(`/closures/${closure.id}/cut-fiber`, { cable_id: cableId, fiber, confirm: confirmForce });
      if (data.ok === false && data.occupied) {
        if (window.confirm(data.detail)) return doCut(cableId, fiber, true);
        return;
      }
      toast.success(`${t("Przecięto włókno")} ${fiber}${data.occupied ? " " + t("(było zajęte)") : ""}`);
      loadPassThrough();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doUncut = async (cableId, fiber) => {
    try {
      await api.post(`/closures/${closure.id}/uncut-fiber`, { cable_id: cableId, fiber });
      toast.success(`${t("Przywrócono włókno przelotowe")} ${fiber}`);
      loadPassThrough();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const allCables = [
    ...connectedCables.map((c) => ({ id: c.id, number: c.number })),
    ...nearbyCables.map((c) => ({ id: c.id, number: c.number })),
    ...passThrough.map((p) => ({ id: p.cable_id, number: p.cable_number })),
  ].filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i);

  const loadSuggest = async () => {
    if (!sugA || !sugB) { toast.error(t("Wybierz dwa kable")); return; }
    if (sugA === sugB) { toast.error(t("Wybierz dwa różne kable")); return; }
    try {
      const { data } = await api.get(`/closures/${closure.id}/suggest-splices`, { params: { cable_a: sugA, cable_b: sugB } });
      setSugg(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const applySuggest = async () => {
    try {
      const { data } = await api.post(`/closures/${closure.id}/splice-auto`, { cable_a_id: sugA, cable_b_id: sugB });
      toast.success(`${t("Utworzono spawów (1:1):")} ${data.created}`);
      setSugg(null);
      api.get(`/closures/${closure.id}/cables`).then((r) => { setConnectedCables(r.data.cables || []); setNearbyCables(r.data.nearby || []); }).catch(() => {});
      api.get("/splices").then((r) => setSplices(r.data.filter((s) => s.closure_id === closure.id))).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const trayCount = Math.max(Number(closure.tray_count) || 1, ...splices.map((s) => Number(s.tray_no) || 1), 1);
  const trays = [];
  for (let t = 1; t <= trayCount; t++) {
    trays.push({ no: t, splices: splices.filter((s) => (Number(s.tray_no) || 1) === t).sort((a, b) => (a.splice_no || 0) - (b.splice_no || 0)) });
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4" data-testid="tray-view">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Layers3 size={18} className="text-purple-400" /> {t("Tacki mufy")} {closure.number}</h3>
          <button onClick={onClose} data-testid="tray-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-5">
          {/* Cables connected to this closure — visible immediately, even without splitters */}
          <div data-testid="closure-cables-section">
            <div className="flex items-center gap-2 mb-2 text-sm font-heading font-bold text-blue-300">
              <Cable size={16} /> {t("Kable w mufie")}
            </div>
            {connectedCables.length === 0 && passThrough.length === 0 && nearbyCables.length === 0 ? (
              <p className="text-xs text-slate-500">{t("Brak podłączonych kabli. Postaw mufę na trasie kabla (auto-podłączenie) lub ustaw „Mufa A/B\" w kablu.")}</p>
            ) : (
              <div className="space-y-2">
                {connectedCables.map((c) => (
                  <div key={c.id} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`closure-cable-${c.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-200 truncate">{t("Kabel")} {c.number}
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500 border border-slate-600 rounded px-1.5 py-0.5">{t("koniec")} {c.end}</span>
                      </div>
                      <div className="text-[11px] text-slate-400">{c.fiber_count} wł. · <span className="text-green-400">{c.free} {t("wolnych")}</span> · <span className="text-red-400">{c.occupied} {t("zajętych")}</span></div>
                    </div>
                    <button type="button" onClick={() => onFibers && onFibers({ id: c.id, number: c.number, cutFibers: [], closureNo: closure.number })}
                      data-testid={`closure-cable-fibers-${c.id}`}
                      className="shrink-0 text-[11px] bg-blue-700 hover:bg-blue-600 text-white rounded px-2.5 py-1.5 flex items-center gap-1">
                      <GitFork size={12} /> {t("Włókna")}
                    </button>
                  </div>
                ))}
                {nearbyCables.map((c) => (
                  <div key={`nb-${c.id}`} className="bg-slate-900 border border-blue-800/40 rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`closure-cable-nearby-${c.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-200 truncate">{t("Kabel")} {c.number}
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-blue-300 border border-blue-700/50 rounded px-1.5 py-0.5">{t("na trasie")}</span>
                      </div>
                      <div className="text-[11px] text-slate-400">{c.fiber_count} wł. · <span className="text-green-400">{c.free} {t("wolnych")}</span> · <span className="text-red-400">{c.occupied} {t("zajętych")}</span></div>
                    </div>
                    <button type="button" onClick={() => onFibers && onFibers({ id: c.id, number: c.number, cutFibers: [], closureNo: closure.number })}
                      data-testid={`closure-cable-fibers-${c.id}`}
                      className="shrink-0 text-[11px] bg-blue-700 hover:bg-blue-600 text-white rounded px-2.5 py-1.5 flex items-center gap-1">
                      <GitFork size={12} /> {t("Włókna")}
                    </button>
                  </div>
                ))}
                {passThrough.map((pt) => (
                  <div key={`pt-${pt.cable_id}`} className="bg-slate-900 border border-cyan-800/50 rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`closure-cable-pt-${pt.cable_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-200 truncate">{t("Kabel")} {pt.cable_number}
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-cyan-300 border border-cyan-700/60 rounded px-1.5 py-0.5">{t("przelotowy")}</span>
                      </div>
                      <div className="text-[11px] text-slate-400">{pt.fiber_count} wł. · {t("przecięte")} {pt.cut_fibers.length}</div>
                    </div>
                    <button type="button" onClick={() => onFibers && onFibers({ id: pt.cable_id, number: pt.cable_number, cutFibers: pt.cut_fibers, closureNo: closure.number })}
                      data-testid={`closure-cable-fibers-${pt.cable_id}`}
                      className="shrink-0 text-[11px] bg-blue-700 hover:bg-blue-600 text-white rounded px-2.5 py-1.5 flex items-center gap-1">
                      <GitFork size={12} /> {t("Włókna")}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Suggest splices between two meeting cables (in closure / on pole) */}
            {canEdit && allCables.length >= 2 && (
              <div className="mt-3 rounded-lg border border-amber-700/40 bg-amber-950/20 p-3" data-testid="closure-suggest-splices">
                <div className="text-xs font-heading font-bold text-amber-300 mb-1 flex items-center gap-1.5"><Split size={13} /> {t("Podpowiedź spawów (spotkanie kabli)")}</div>
                <p className="text-[11px] text-slate-400 mb-2">{t("Wybierz dwa kable, aby zobaczyć wolne włókna do pospawania 1:1 (np. przejście do innej ulicy/miejscowości).")}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={sugA} onChange={(e) => { setSugA(e.target.value); setSugg(null); }} data-testid="suggest-cable-a" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs">
                    <option value="">{t("— kabel A —")}</option>
                    {allCables.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
                  </select>
                  <span className="text-slate-500 text-xs">↔</span>
                  <select value={sugB} onChange={(e) => { setSugB(e.target.value); setSugg(null); }} data-testid="suggest-cable-b" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs">
                    <option value="">{t("— kabel B —")}</option>
                    {allCables.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
                  </select>
                  <button onClick={loadSuggest} data-testid="suggest-splices-btn" className="bg-amber-600 hover:bg-amber-500 text-white rounded px-2.5 py-1.5 text-xs">{t("Podpowiedz")}</button>
                </div>
                {sugg && (
                  <div className="mt-2 text-xs">
                    <p className="text-slate-300 mb-1">{sugg.note}</p>
                    {sugg.count > 0 && (
                      <>
                        <div className="text-slate-400">{t("Wolne włókna 1:1")}: <b className="text-emerald-300">{sugg.count}</b> — {sugg.pairs.slice(0, 12).map((p) => p.fiber).join(", ")}{sugg.pairs.length > 12 ? "…" : ""}</div>
                        {sugg.hints?.length > 0 && <div className="text-slate-500 mt-1">{t("Prowadzą do")}: {sugg.hints.join(" · ")}</div>}
                        <button onClick={applySuggest} data-testid="suggest-apply-btn" className="mt-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded px-2.5 py-1 text-xs">{t("Zastosuj auto-spaw")}</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pass-through cables (uncut fibers passing through the closure) */}
          {passThrough.length > 0 && (
            <div data-testid="passthrough-section">
              <div className="flex items-center gap-2 mb-2 text-sm font-heading font-bold text-cyan-300">
                <Split size={16} /> {t("Kable przelotowe (pass-through)")}
              </div>
              <p className="text-[11px] text-slate-400 mb-3">{t("Kabel przechodzi przez mufę bez cięcia. Kliknij włókno, aby je przeciąć i skierować do splittera. Zajęte włókna (niosące sygnał) wymagają potwierdzenia.")}</p>
              <div className="space-y-3">
                {passThrough.map((pt) => (
                  <div key={pt.cable_id} className="bg-slate-900 border border-slate-700 rounded-lg p-3" data-testid={`passthrough-cable-${pt.cable_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-slate-200">{t("Kabel")} {pt.cable_number} <span className="text-slate-500 font-normal">· {pt.fiber_count} wł.</span></span>
                      <span className="text-[11px] text-slate-400">{t("Przecięte:")} <b className="text-orange-300">{pt.cut_fibers.length}</b> / {t("przelotowe:")} <b className="text-cyan-300">{pt.fiber_count - pt.cut_fibers.length}</b></span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {pt.fibers.map((f) => {
                        const isCut = pt.cut_fibers.includes(f.number);
                        const occupied = f.status === "zajęte" || f.status === "zajete";
                        return (
                          <button key={f.number} type="button" disabled={!canEdit}
                            data-testid={`ptfiber-${pt.cable_id}-${f.number}`}
                            onClick={() => (isCut ? doUncut(pt.cable_id, f.number) : doCut(pt.cable_id, f.number))}
                            title={isCut ? `${t("Włókno")} ${f.number} ${t("przecięte — kliknij, aby przywrócić przelot")}` : `${t("Włókno")} ${f.number} (${f.color})${occupied ? ` — ${t("zajęte →")} ${f.leads_to || "—"}` : " — " + t("przelotowe, kliknij aby przeciąć")}`}
                            className={`relative w-9 h-9 rounded flex items-center justify-center border text-[10px] font-mono transition-colors
                              ${isCut ? "border-orange-500 bg-orange-500/15 text-orange-300"
                                : "border-slate-600 hover:border-cyan-400 bg-slate-800 text-slate-200"} disabled:opacity-60 disabled:cursor-not-allowed`}>
                            <span className="w-2.5 h-2.5 rounded-full border border-slate-900 absolute top-0.5 left-0.5" style={{ background: colorForFiber(f.number) }} />
                            {f.number}
                            {isCut ? <Scissors size={9} className="absolute bottom-0.5 right-0.5 text-orange-300" />
                              : occupied ? <Lock size={9} className="absolute bottom-0.5 right-0.5 text-red-400" /> : null}
                          </button>
                        );
                      })}
                    </div>
                    {pt.cut_fibers.length > 0 && canEdit && (
                      <button type="button" onClick={() => pt.cut_fibers.forEach((n) => doUncut(pt.cable_id, n))}
                        data-testid={`ptrestore-${pt.cable_id}`}
                        className="mt-2 text-[11px] text-slate-400 hover:text-cyan-300 flex items-center gap-1">
                        <RotateCcw size={12} /> Przywróć wszystkie przeloty
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading ? <div className="text-slate-500 text-sm">{t("Ładowanie…")}</div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trays.map((tray) => {
                const cap = Math.max(1, Number(closure.tray_capacity) || 12);
                return (
                  <div key={tray.no} className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden" data-testid={`tray-${tray.no}`}>
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-800 border-b border-slate-700">
                      <span className="text-sm font-heading font-bold">{t("Tacka")} {tray.no}</span>
                      <span className="text-xs text-slate-400 font-mono">{tray.splices.length}/{cap} {t("spawów")}</span>
                    </div>
                    <div className="p-2 space-y-1">
                      {tray.splices.length === 0 && <div className="text-xs text-slate-600 text-center py-3">{t("Tacka pusta")}</div>}
                      {tray.splices.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 bg-slate-800/60 rounded px-2 py-1.5">
                          <span className="text-[11px] text-slate-500 font-mono w-6">#{s.splice_no}</span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full border border-slate-600" style={{ background: colorForFiber(s.fiber_in) }} />
                            <span className="text-xs font-mono">{s.fiber_in}</span>
                          </span>
                          <GitFork size={13} className="text-pink-400 rotate-90" />
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full border border-slate-600" style={{ background: colorForFiber(s.fiber_out) }} />
                            <span className="text-xs font-mono">{s.fiber_out}</span>
                          </span>
                          {s.color && <span className="text-[10px] text-slate-400 ml-auto">{s.color}</span>}
                          {s.attenuation_db != null && <span className="text-[10px] text-cyan-400 font-mono">{Number(s.attenuation_db).toFixed(2)} dB</span>}
                        </div>
                      ))}
                      {/* free slots */}
                      {Array.from({ length: Math.max(0, cap - tray.splices.length) }).slice(0, 12).map((_, i) => (
                        <div key={`e${i}`} className="h-6 rounded border border-dashed border-slate-800" />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
